import { onSweepClicked } from '../../util/commandActions';
import { IStyleFunctionOrObject } from '@fluentui/react';
import { ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { AppInput, constructKeytip, getKeytipString, RibbonIconColor } from 'owa-command-ribbon';
import { getIsDarkTheme } from 'owa-fabric-theme';
import Folder from 'owa-fluent-icons-svg/lib/icons/FolderRegular';
import { getCustomIcon } from 'owa-folders-common';
import getArchiveFolderTreeRootFolder from 'owa-folders/lib/selectors/getArchiveFolderTreeRootFolder';
import FolderTreeData from 'owa-folders/lib/store/schema/FolderTreeData';
import loc from 'owa-localize';
import MoveToDifferentStrings from 'owa-locstrings/lib/strings/movetoadifferentfolder.locstring.json';
import { getMailMenuItemShouldShowMap } from 'owa-mail-filterable-menu-behavior';
import { MenuItemType } from 'owa-filterable-menu';
import { listViewStore } from 'owa-mail-list-store';
import CreateFolderStrings from 'owa-mail-moveto-control/lib/components/NewFolderMenuItem.locstring.json';
import { getAllSharedFoldersData } from 'owa-mail-moveto-control/lib/helpers/getAllSharedFoldersData';
import getFolderNameToCountMap from 'owa-mail-moveto-control/lib/helpers/getFolderNameToCountMap';
import { getFoldersForGivenMailboxType } from 'owa-mail-moveto-control/lib/helpers/getFoldersForMoveTo';
import 'owa-mail-moveto-control/lib/mutators';
import 'owa-mail-moveto-control/lib/orchestrators';
import moveToStore from 'owa-mail-moveto-control/lib/store/store';
import MoveToStrings from 'owa-mail-movetofolder-view/lib/components/getMoveToMenuProperties.locstring.json';
import { isFocusedInboxOverridden } from 'owa-mail-movetofolder-view/lib/utils/isFocusedInboxOverridden';
import { getStringRibbonId, MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import type { AppInputProps } from '@1js/acui-input/lib/components/AppInput';
import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { MenuItemControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/controls/Menu';
import type { RibbonFlyoutAnchorProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon';
import type { IButtonStyles } from '@fluentui/react/lib/Button';
import type { IKeytipProps } from '@fluentui/react/lib/Keytip';
import type { MailboxType } from 'owa-graph-schema';
import {
    onChangeMoveToMailboxType,
    onSearchTextChanged,
    onViewAllMenuClicked,
} from 'owa-mail-moveto-control/lib/actions/internalActions';
import {
    getFolderNameToShow,
    shouldShowAllFoldersMenuItem,
} from 'owa-mail-moveto-control/lib/helpers/moveToCommonUtils';
import {
    getMoveToNewFolderText,
    getNewFolderTextFromFindText,
    getSearchBoxLabelText,
} from 'owa-mail-moveto-control/lib/helpers/moveToStringUtils';
import type MoveToFoldersType from 'owa-mail-moveto-control/lib/store/schema/MoveToFoldersType';
import type MoveToFolderType from 'owa-mail-moveto-control/lib/store/schema/MoveToFolderType';
import {
    getRibbonIconModeColor,
    RibbonIconMode,
} from 'owa-command-ribbon/lib/util/ribbonIconModeColors';
import {
    createMailAppSearchBox,
    createMailGeneratedMenuItem,
} from '../../util/createMailRibbonButton';
import {
    onAlwaysMoveToFocusedOtherInboxClicked,
    onCreateNewFolderClicked,
    onMoveToFocusedOtherInboxClicked,
    onMoveToFolderClicked,
} from '../../util/onMoveToCommandActions';
import { isFeatureEnabled } from 'owa-feature-flags';

const MAX_MOVETO_ITEMS_MRU: number = 8;
const MOVE_TO_MENU_WIDTH: number = 266;
const SHOW_ALL: string = ' '; // Note: there is no way for a UI user to set this value

// trimLeft on strings is supported in lib es2019.  We are curently on es2017
function trimLeft(untrimmedString: string): string {
    if (untrimmedString.length > 0 && untrimmedString[0] == ' ') {
        return trimLeft(untrimmedString.substr(1));
    }

    return untrimmedString;
}

/**
 * The Move to menu contains three sections:
 * (1) A filterable set of folders
 * (2) A section having one button to create a new folder, either from scratch, or starting
 * from the contents of the folder search box.
 * (3) A section with a few additional options, including Focused Inbox and a rule to "move all message from..."
 */
export default function getMoveToSections(keytipTabId: MailRibbonTabId | undefined): MenuSection[] {
    const folderControls: MenuItemControlProps[] = getMoveFoldersSection();
    const folderShown: number = folderControls.length - 1; // subtract out the search box control

    return [
        { controls: folderControls },
        {
            controls: getNewFolderSection(trimLeft(moveToStore.findText), keytipTabId),
        },
        {
            controls: getMoveToOtherFolderSection(folderShown, keytipTabId),
        },
    ];
}

export function resetMoveToSearch(onEnter: boolean) {
    // The space is necessary on exit so that if the user now opens the message list r-click
    // MOVE menu, we won't show ALL user mailbox folders
    const searchText: string = onEnter ? '' : ' ';
    onSearchTextChanged(moveToStore.findText, searchText);
    onChangeMoveToMailboxType('UserMailbox');
}

/*
 * Note: paddingRight SHOULD be 6px. For now, setting it to 10px to match the
 * (unchangable) padding on flyout buttons in menus. Fix for the real issue
 * is tracked by https://office.visualstudio.com/OC/_workitems/edit/5083823
 */
function getMoveToMenuStyle(paddingLeft: string): IButtonStyles {
    return {
        root: {
            width: MOVE_TO_MENU_WIDTH,
        },
        label: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        icon: {
            paddingTop: '3px',
            paddingRight: '10px',
            paddingLeft,
            textAlign: 'center',
        },
    };
}

function getCreateNewFolderStyle(): IStyleFunctionOrObject<ITextFieldStyleProps, ITextFieldStyles> {
    return {
        root: {
            paddingLeft: '8px',
            paddingRight: '8px',
        },
        field: {
            paddingLeft: '32px',
        },
        fieldGroup: {
            borderWidth: '0px',
        },
    };
}

const createMoveToMenuButton = (
    id: string,
    label: string,
    onExecute: () => void,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    depth?: number,
    customTooltip?: string,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (159,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): MenuItemControlProps => {
    const paddingSize: number = depth ? depth * 16 : 0;
    const paddingLeft: string = paddingSize + 'px';

    return createMailGeneratedMenuItem(
        id,
        label,
        customTooltip,
        onExecute,
        keytipProps,
        undefined /*isSelected*/,
        icon,
        iconColor,
        getMoveToMenuStyle(paddingLeft),
        overrides
    );
};

function getMoveToFocusedOtherInboxSection(
    sectionControls: MenuItemControlProps[],
    keytipTabId: MailRibbonTabId | undefined
): MenuItemControlProps[] {
    const shouldShowBehaviorMap = getMailMenuItemShouldShowMap();

    // Add "Move to Focused/Other" section
    if (shouldShowBehaviorMap[MenuItemType.MoveToFocusedInbox]()) {
        sectionControls.push(
            createMoveToMenuButton(
                getStringRibbonId(MailRibbonControlId.MoveToFocusedInbox),
                loc(MoveToStrings.moveToFocusedInboxText),
                () => {
                    onMoveToFocusedOtherInboxClicked(FocusedViewFilter.Focused);
                },
                constructKeytip(
                    keytipTabId
                        ? [getKeytipString(keytipTabId), getKeytipString(MailRibbonControlId.Move)]
                        : [getKeytipString(MailRibbonControlId.Move)],
                    getKeytipString(MailRibbonControlId.MoveToFocusedInbox)
                )
            )
        );
    } else if (shouldShowBehaviorMap[MenuItemType.MoveToOtherInbox]()) {
        sectionControls.push(
            createMoveToMenuButton(
                getStringRibbonId(MailRibbonControlId.MoveToOtherInbox),
                loc(MoveToStrings.moveToOtherInboxText),
                () => {
                    onMoveToFocusedOtherInboxClicked(FocusedViewFilter.Other);
                },
                constructKeytip(
                    keytipTabId
                        ? [getKeytipString(keytipTabId), getKeytipString(MailRibbonControlId.Move)]
                        : [getKeytipString(MailRibbonControlId.Move)],
                    getKeytipString(MailRibbonControlId.MoveToOtherInbox)
                )
            )
        );
    }

    // Add "Always Move to Focused/Other" section
    if (shouldShowBehaviorMap[MenuItemType.AlwaysMoveToFocusedInbox]()) {
        sectionControls.push(
            createMoveToMenuButton(
                getStringRibbonId(MailRibbonControlId.AlwaysMoveToFocusedInbox),
                loc(MoveToStrings.alwaysMoveToFocusedInboxText),
                () => {
                    onAlwaysMoveToFocusedOtherInboxClicked(FocusedViewFilter.Focused);
                },
                constructKeytip(
                    keytipTabId
                        ? [getKeytipString(keytipTabId), getKeytipString(MailRibbonControlId.Move)]
                        : [getKeytipString(MailRibbonControlId.Move)],
                    getKeytipString(MailRibbonControlId.AlwaysMoveToFocusedInbox)
                )
            )
        );
    } else if (shouldShowBehaviorMap[MenuItemType.AlwaysMoveToOtherInbox]()) {
        const tableViewId = listViewStore.selectedTableViewId;
        const tableView = listViewStore.tableViews.get(tableViewId)!;
        const rowKeys = [...tableView.selectedRowKeys.keys()];
        const shouldDisable = isFocusedInboxOverridden(rowKeys[0], tableView);

        sectionControls.push(
            createMoveToMenuButton(
                getStringRibbonId(MailRibbonControlId.AlwaysMoveToOtherInbox),
                loc(MoveToStrings.alwaysMoveToOtherInboxText),
                () => {
                    onAlwaysMoveToFocusedOtherInboxClicked(FocusedViewFilter.Other);
                },
                constructKeytip(
                    keytipTabId
                        ? [getKeytipString(keytipTabId), getKeytipString(MailRibbonControlId.Move)]
                        : [getKeytipString(MailRibbonControlId.Move)],
                    getKeytipString(MailRibbonControlId.AlwaysMoveToOtherInbox)
                ),
                undefined /* icon */,
                undefined /*iconColor*/,
                undefined /* depth */,
                undefined /*customTooltip*/,
                {
                    disable: shouldDisable,
                    title: shouldDisable
                        ? loc(MoveToStrings.moveToOtherDisabledDueToOverride)
                        : undefined,
                }
            )
        );
    }

    return sectionControls;
}

function getMoveFoldersSection(): MenuItemControlProps[] {
    let sectionControls: MenuItemControlProps[] = [];

    // Add filtering search box
    function onSearchBoxChange(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) {
        const newSearchBoxText: string = newValue ? trimLeft(newValue) : '';
        onSearchTextChanged(moveToStore.findText, newSearchBoxText);
    }

    const moveToMailboxType = moveToStore.moveToMailboxType;
    const trimmedSearchText: string = trimLeft(moveToStore.findText);

    sectionControls.push(
        createMailAppSearchBox(
            'moveToMenu_SearchBox',
            getSearchBoxLabelText(moveToMailboxType),
            onSearchBoxChange,
            undefined /*keytips*/,
            undefined /*icon*/,
            undefined /*iconColor*/,
            { value: trimmedSearchText }
        )
    );

    // Get filtered folders for given mailbox type
    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;
    const selectedFolderId = tableView.tableQuery.folderId;
    const showAllFolders: boolean = moveToStore.findText === SHOW_ALL;
    let moveToFolder: MoveToFoldersType = { folders: [], hasExactMatch: false };
    let supportedMailboxTypes: MailboxType[] = [];

    if (isFeatureEnabled('grp-loadFolders')) {
        supportedMailboxTypes.push('GroupMailbox');
    }

    moveToFolder = getFoldersForGivenMailboxType(
        moveToMailboxType,
        trimmedSearchText,
        showAllFolders,
        undefined /*shouldShowRootFolder*/,
        !showAllFolders /*disableSelectedFolder*/,
        selectedFolderId,
        supportedMailboxTypes
    );

    // Add folders to sectionControls
    const folderNameCountMap = getFolderNameToCountMap(moveToFolder.folders);
    const maxItemsToShow: number = showAllFolders
        ? moveToFolder.folders.length
        : Math.min(MAX_MOVETO_ITEMS_MRU, moveToFolder.folders.length);
    const theme = getIsDarkTheme() ? RibbonIconMode.dark : RibbonIconMode.light;

    for (let folderIdx = 0; folderIdx < maxItemsToShow; folderIdx++) {
        const folder: MoveToFolderType = moveToFolder.folders[folderIdx];
        const folderId: string = folder.folderId;
        const isCurrentFolder: boolean = selectedFolderId == folderId;

        // If not showing all folders, hide the current folder
        if (isCurrentFolder && !showAllFolders) {
            continue;
        }

        const iconColor: string | undefined = getIconColorBasedOnFolderId(theme, folderId);
        const folderNameToShow: string = getFolderNameToShow(folderNameCountMap, folder);
        const buttonId: string =
            (showAllFolders
                ? 'moveTo' + moveToStore.moveToMailboxType + 'Folder_'
                : 'moveToMRUFolder_') + folderId;

        sectionControls.push(
            createMoveToMenuButton(
                buttonId,
                folderNameToShow,
                () => {
                    onMoveToFolderClicked(folderId);
                },
                undefined /* keytipProps */,
                getCustomIcon(
                    folderId,
                    true /* showNonSpecialFolderIcon */,
                    true /* isFromRibbon */
                ),
                iconColor,
                showAllFolders ? folder.depth : undefined,
                undefined /*customTooltip*/,
                { disabled: isCurrentFolder }
            )
        );
    }

    return sectionControls;
}

const createNewFolderButton = (keytipTabId: MailRibbonTabId | undefined): AppInputProps => {
    return {
        type: AppInput,
        id: getStringRibbonId(MailRibbonControlId.MoveToCreateNewFolder),
        placeholder: getMoveToNewFolderText(moveToStore.moveToMailboxType),
        onExecute: onCreateNewFolderClicked,
        keytipProps: constructKeytip(
            keytipTabId
                ? [getKeytipString(keytipTabId), getKeytipString(MailRibbonControlId.Move)]
                : [getKeytipString(MailRibbonControlId.Move)],
            getKeytipString(MailRibbonControlId.MoveToCreateNewFolder)
        ),
        styles: getCreateNewFolderStyle(),
        title: loc(CreateFolderStrings.moveToFolderCreateNewTooltip),
        executeOnEnter: true,
        selectTextOnFocus: false,
    };
};

function getNewFolderSection(
    searchText: string,
    keytipTabId: MailRibbonTabId | undefined
): MenuItemControlProps[] {
    let sectionControls: MenuItemControlProps[] = [];

    // Decision if new folder option should be present or not.
    const newFolderShouldHide: boolean = moveToStore.moveToMailboxType === 'SharedMailbox';
    if (newFolderShouldHide) {
        return sectionControls;
    }

    if (searchText.length > 0) {
        sectionControls.push(
            createMoveToMenuButton(
                getStringRibbonId(MailRibbonControlId.MoveToCreateFolderFromString),
                `"${searchText}" ${getNewFolderTextFromFindText(moveToStore.moveToMailboxType)}`,
                () => {
                    onCreateNewFolderClicked(searchText);
                },
                undefined /*keytip*/,
                Folder,
                undefined /*iconColor*/,
                undefined /*depth*/,
                CreateFolderStrings.moveToFolderCreateNewTooltip
            )
        );
    } else {
        sectionControls.push(createNewFolderButton(keytipTabId));
    }

    return sectionControls;
}

function getMoveToOtherFolderSection(
    foldersShownCount: number,
    keytipTabId: MailRibbonTabId | undefined
): (MenuItemControlProps | RibbonFlyoutAnchorProps)[] {
    let sectionControls: (MenuItemControlProps | RibbonFlyoutAnchorProps)[] = [];

    // Add Focused Inbox / Other Folder, if in that mode
    getMoveToFocusedOtherInboxSection(sectionControls, keytipTabId);

    // shouldShowAllFoldersMenuItem() requires "folders shown", but only for the count. Fake it here.
    const foldersShown: MoveToFolderType[] = new Array(foldersShownCount);
    const supportedMailboxTypes: MailboxType[] = ['UserMailbox'];

    if (!isConsumer()) {
        supportedMailboxTypes.push('ArchiveMailbox', 'SharedMailbox');
    }

    if (shouldShowAllFoldersMenuItem('UserMailbox', supportedMailboxTypes, foldersShown)) {
        sectionControls.push(
            createMoveToMenuButton(
                getStringRibbonId(MailRibbonControlId.MoveToDifferentFolder),
                loc(MoveToDifferentStrings.moveToADifferentFolder),
                () => {
                    const ele: HTMLElement | null = document.getElementById('moveToMenu_SearchBox');

                    if (ele) {
                        ele.focus();
                    }

                    onViewAllMenuClicked('UserMailbox');
                    onSearchTextChanged(moveToStore.findText, SHOW_ALL);
                },
                constructKeytip(
                    keytipTabId
                        ? [getKeytipString(keytipTabId), getKeytipString(MailRibbonControlId.Move)]
                        : [getKeytipString(MailRibbonControlId.Move)],
                    getKeytipString(MailRibbonControlId.MoveToDifferentFolder)
                ),
                undefined /*icon*/,
                undefined /*iconColor*/,
                undefined /*depth*/,
                undefined /*customTooltip*/,
                { dismissHostingSurfaceOnExecute: false }
            )
        );
    }

    if (shouldShowAllFoldersMenuItem('ArchiveMailbox', supportedMailboxTypes, foldersShown)) {
        sectionControls.push(
            createMoveToMenuButton(
                getStringRibbonId(MailRibbonControlId.MoveToArchiveFolder),
                getArchiveFolderTreeRootFolder().displayName,
                () => {
                    const ele: HTMLElement | null = document.getElementById('moveToMenu_SearchBox');

                    if (ele) {
                        ele.focus();
                    }

                    onViewAllMenuClicked('ArchiveMailbox');
                    onSearchTextChanged(moveToStore.findText, SHOW_ALL);
                },
                undefined /*keytip*/,
                undefined /*icon*/,
                undefined /*iconColor*/,
                undefined /*depth*/,
                undefined /*customTooltip*/,
                { dismissHostingSurfaceOnExecute: false }
            )
        );
    }

    if (shouldShowAllFoldersMenuItem('SharedMailbox', supportedMailboxTypes, foldersShown)) {
        const allSharedFoldersData: FolderTreeData[] = getAllSharedFoldersData();

        // For each shared folder root push an option in move to control
        allSharedFoldersData.forEach(sharedFolder => {
            const {
                displayName: displayName,
                principalSMTPAddress: folderId, // We are assuming principalSMTPAddress as folderId in moveto control
            } = sharedFolder.rootFolder;

            // Do not show that shared folder which is already selected to show all of its child folders.
            // Ex. - If shared folder person 'PersonFoo' is currently selected then moveTo options shouldn't include 'foo' as it's already opened.
            if (
                moveToStore.moveToMailboxType == 'SharedMailbox' &&
                moveToStore.moveToSharedFolderRootId === folderId
            ) {
                return;
            }

            sectionControls.push(
                createMoveToMenuButton(
                    'MoveTo_SharedMailBox_' + displayName,
                    displayName,
                    () => {
                        const ele: HTMLElement | null = document.getElementById(
                            'moveToMenu_SearchBox'
                        );

                        if (ele) {
                            ele.focus();
                        }

                        onViewAllMenuClicked(
                            'SharedMailbox',
                            folderId ? (folderId as string) : undefined
                        );

                        onSearchTextChanged(moveToStore.findText, SHOW_ALL);
                    },
                    undefined /*keytip*/,
                    undefined /*icon*/,
                    undefined /*iconColor*/,
                    undefined /*depth*/,
                    undefined /*customTooltip*/,
                    { dismissHostingSurfaceOnExecute: false }
                )
            );
        });
    }

    sectionControls.push(
        createMoveToMenuButton(
            getStringRibbonId(MailRibbonControlId.MoveAllMessagesFrom),
            loc(MoveToStrings.moveToFolderMoveAllFrom),
            onSweepClicked,
            constructKeytip(
                keytipTabId
                    ? [getKeytipString(keytipTabId), getKeytipString(MailRibbonControlId.Move)]
                    : [getKeytipString(MailRibbonControlId.Move)],
                getKeytipString(MailRibbonControlId.MoveAllMessagesFrom)
            )
        )
    );

    return sectionControls;
}

function getIconColorBasedOnFolderId(theme: RibbonIconMode, folderId: string): string | undefined {
    let ribbonIconColor: RibbonIconColor;

    switch (folderId) {
        case folderNameToId('archive'):
            ribbonIconColor = RibbonIconColor.green;
            break;

        case folderNameToId('drafts'):
            ribbonIconColor = RibbonIconColor.blue;
            break;

        case folderNameToId('junkemail'):
            ribbonIconColor = RibbonIconColor.red;
            break;

        default:
            return undefined;
    }

    return getRibbonIconModeColor(theme, ribbonIconColor);
}
